import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import {
  getIdToken,
  getRefreshToken,
  isTokenNearExpiry,
  hasValidTokens,
  clearAllTokens,
} from "../auth";
import { EmbeddedAuthError } from "./errors";
import { getNestedValue, isAuthErrorMessage } from "./utils";
import type {
  HttpClientConfig,
  HttpClientRequestPayload,
  HttpClientRequestHeaders,
  HttpClientResponse,
  HttpClientErrorResponse,
  EmbeddedErrorResponse,
} from "./types";

// Module-level SSO function references (to avoid circular dependencies)
let refreshAccessTokenFunction:
  | ((refreshToken: string) => Promise<void>)
  | null = null;
let startPKCEFlowFunction: (() => Promise<void>) | null = null;
let onMaxRetriesExhaustedCallback: (() => void) | null = null;

/**
 * Register SSO functions with the HTTP client for automatic token refresh.
 * This should be called from your SSO module to avoid circular dependencies.
 */
export function setSSOFunctions(
  refreshFn: (refreshToken: string) => Promise<void>,
  startPKCEFn: () => Promise<void>,
): void {
  refreshAccessTokenFunction = refreshFn;
  startPKCEFlowFunction = startPKCEFn;
}

/**
 * Set callback for when max retries are exhausted.
 * Use this to show error modals or take other actions.
 */
export function setOnMaxRetriesExhausted(callback: () => void): void {
  onMaxRetriesExhaustedCallback = callback;
}

/**
 * HTTP client with automatic token refresh and retry logic.
 *
 * Features:
 * - Automatic Bearer token injection
 * - Proactive token refresh when near expiry
 * - Retry logic with request queuing during token refresh
 * - Embedded auth error detection (HTTP 200 with 401/403 in body)
 * - Configurable data extraction path
 */
export class HttpClient {
  private axiosInstance: AxiosInstance;
  private dataExtractor: string;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
    config: AxiosRequestConfig;
  }> = [];

  constructor(config: HttpClientConfig) {
    const { baseURL, dataExtractor = "data.data", defaultHeaders } = config;

    this.dataExtractor = dataExtractor;

    // Get deployment color from build-time environment variable
    // Defaults to 'blue' if not set (backward compatibility)
    const deploymentColor = import.meta.env?.VITE_DEPLOYMENT_COLOR || "blue";

    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        "X-Deployment-Id": deploymentColor,
        ...defaultHeaders,
      },
    });
  }

  /**
   * Check if all required tokens exist and are valid
   */
  private hasValidTokens(): boolean {
    return hasValidTokens();
  }

  /**
   * Process all queued requests after token refresh
   */
  private processQueue(error?: Error): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    this.failedQueue = [];
  }

  /**
   * Handle token refresh with queue management
   */
  private async handleTokenRefresh(): Promise<void> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({
          resolve: () => resolve(),
          reject,
          config: {},
        });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      if (!refreshAccessTokenFunction) {
        throw new Error(
          "SSO functions not registered. Call setSSOFunctions first.",
        );
      }

      await refreshAccessTokenFunction(refreshToken);

      const newToken = getIdToken();

      if (!newToken) {
        throw new Error("Token refresh completed but no new token received");
      }

      this.isRefreshing = false;
      this.processQueue();
    } catch (error) {
      this.isRefreshing = false;
      this.processQueue(error as Error);

      // Clear tokens and restart auth flow
      clearAllTokens();

      // Small delay to ensure storage is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (startPKCEFlowFunction) {
        await startPKCEFlowFunction();
      }

      throw error;
    }
  }

  /**
   * Check if response contains embedded auth error (HTTP 200 with 401/403 in body)
   */
  private checkForEmbeddedAuthError(
    response: AxiosResponse,
    url: string,
  ): void {
    const data = response.data;

    if (!data || typeof data !== "object") {
      return;
    }

    // Check for embedded status codes
    const embeddedStatus = (data as EmbeddedErrorResponse).status;
    const embeddedError = (data as EmbeddedErrorResponse).error;

    if (embeddedStatus === 401 || embeddedStatus === 403) {
      throw new EmbeddedAuthError(data as EmbeddedErrorResponse, url);
    }

    if (
      embeddedError &&
      (embeddedError.toLowerCase() === "unauthorized" ||
        embeddedError.toLowerCase() === "forbidden")
    ) {
      throw new EmbeddedAuthError(data as EmbeddedErrorResponse, url);
    }
  }

  /**
   * Create a success response object
   */
  // private createSuccessResponse<T>(
  //   response: AxiosResponse,
  //   responseType?: AxiosRequestConfig["responseType"],
  // ): HttpClientResponse<T> {
  //   let data: unknown;

  //   // For binary responses, return raw data
  //   if (responseType === "arraybuffer" || responseType === "blob") {
  //     data = response.data;
  //   } else {
  //     // Extract data using configured path
  //     data = getNestedValue(response, this.dataExtractor);
  //   }

  //   return {
  //     success: true,
  //     status: response.status,
  //     statusText: response.statusText,
  //     message: "Request successful",
  //     headers: response.headers,
  //     errors: [],
  //     data: data as T,
  //   };
  // }

  private createSuccessResponse<T>(
    response: AxiosResponse,
    responseType?: AxiosRequestConfig["responseType"],
  ): HttpClientResponse<T> {
    let data: unknown;

    // Handle binary responses
    if (responseType === "arraybuffer" || responseType === "blob") {
      data = response.data;
    } else {
      data = getNestedValue(response, this.dataExtractor);
    }

    const responseBody = response.data as
      | {
          success?: boolean;
          message?: string;
          errors?: unknown[];
        }
      | undefined;

    return {
      success: responseBody?.success ?? true,
      status: response.status,
      statusText: response.statusText,
      message: responseBody?.message ?? "Request successful",
      headers: response.headers,
      errors: responseBody?.errors ?? [],
      data: data as T,
    };
  }

  /**
   * Create an error response object
   */
  static createErrorResponse<T>(
    error: AxiosError | EmbeddedAuthError | Error,
    retryCount: number = 0,
  ): HttpClientErrorResponse<T> {
    if (error instanceof EmbeddedAuthError) {
      return {
        success: false,
        status: error.response.status,
        statusText: error.response.statusText,
        message: error.message,
        headers: {},
        errors: [error.response.data],
        errorDetails: JSON.stringify(error.response.data),
        data: error.response.data as T,
        retryCount,
        isAuthError: true,
        isEmbeddedAuthError: true,
      };
    }

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status ?? null;
      const isAuthError = status === 401 || status === 403;

      let message = "An error occurred";
      if (axiosError.code === "ECONNABORTED") {
        message = "Request timeout";
      } else if (!axiosError.response) {
        message = "Network error - please check your connection";
      } else {
        const responseData = axiosError.response?.data as
          | { message?: string }
          | undefined;
        message = responseData?.message || axiosError.message;
      }

      return {
        success: false,
        status,
        statusText: axiosError.response?.statusText ?? "",
        message,
        headers: axiosError.response?.headers ?? {},
        errors: axiosError.response?.data ? [axiosError.response.data] : [],
        errorDetails: JSON.stringify(axiosError.response?.data),
        data: (axiosError.response?.data as T) ?? null,
        retryCount,
        isAuthError,
      };
    }

    // Generic error
    return {
      success: false,
      status: null,
      statusText: "",
      message: error.message || "An unknown error occurred",
      headers: {},
      errors: [],
      data: null,
      retryCount,
      isAuthError: false,
    };
  }

  /**
   * Execute an HTTP request with retry logic
   */
  private async executeRequest<T>(
    method: "get" | "post" | "put" | "patch" | "delete",
    url: string,
    data?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
    responseType?: AxiosRequestConfig["responseType"],
    config?: AxiosRequestConfig,
    authHeader?: Record<string, string>,
    retryCount: number = 0,
  ): Promise<unknown> {
    const token = getIdToken();

    // If no auth header provided and we have a token, use it
    const finalAuthHeader =
      authHeader ?? (token ? { Authorization: `Bearer ${token}` } : {});

    // Proactive token refresh if near expiry
    if (token && isTokenNearExpiry() && !this.isRefreshing) {
      try {
        await this.handleTokenRefresh();
        const newToken = getIdToken();
        return this.executeRequest<T>(
          method,
          url,
          data,
          headers,
          responseType,
          config,
          newToken ? { Authorization: `Bearer ${newToken}` } : {},
        );
      } catch (refreshError) {
        console.warn("Proactive token refresh failed:", refreshError);
        // Continue with current token
      }
    }

    try {
      const requestConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          ...finalAuthHeader,
          ...headers,
        },
        responseType,
      };

      let response: AxiosResponse;

      switch (method) {
        case "get":
          response = await this.axiosInstance.get(url, {
            ...requestConfig,
            params: data,
          });
          break;
        case "post":
          response = await this.axiosInstance.post(url, data, requestConfig);
          break;
        case "put":
          response = await this.axiosInstance.put(url, data, requestConfig);
          break;
        case "patch":
          response = await this.axiosInstance.patch(url, data, requestConfig);
          break;
        case "delete":
          response = await this.axiosInstance.delete(url, {
            ...requestConfig,
            params: data,
          });
          break;
      }

      // Check for embedded auth errors
      this.checkForEmbeddedAuthError(response, url);

      return this.createSuccessResponse<T>(response, responseType);
    } catch (error) {
      const isEmbedded = error instanceof EmbeddedAuthError;
      const axiosError = error as AxiosError;
      const status = isEmbedded
        ? (error as EmbeddedAuthError).response.status
        : axiosError.response?.status;
      const isAuthStatus = status === 401 || status === 403;
      const errorMessage = axiosError.message || "";
      const isAuthMessage = isAuthErrorMessage(errorMessage);

      // Embedded auth errors: retry without token refresh (backend issue)
      if (isEmbedded && retryCount < 3) {
        console.warn(`Embedded auth error, retrying (${retryCount + 1}/3)...`);
        return this.executeRequest<T>(
          method,
          url,
          data,
          headers,
          responseType,
          config,
          authHeader,
          retryCount + 1,
        );
      }

      // Standard auth errors: refresh token and retry
      if ((isAuthStatus || isAuthMessage) && !isEmbedded && retryCount < 3) {
        try {
          await this.handleTokenRefresh();
          const newToken = getIdToken();
          return this.executeRequest<T>(
            method,
            url,
            data,
            headers,
            responseType,
            config,
            newToken ? { Authorization: `Bearer ${newToken}` } : {},
            retryCount + 1,
          );
        } catch (refreshError) {
          // Token refresh failed, return original error
          if (onMaxRetriesExhaustedCallback) {
            onMaxRetriesExhaustedCallback();
          }
          return HttpClient.createErrorResponse(
            error as AxiosError | Error,
            retryCount,
          );
        }
      }

      // Max retries exhausted
      if (retryCount >= 3 && onMaxRetriesExhaustedCallback) {
        onMaxRetriesExhaustedCallback();
      }

      return HttpClient.createErrorResponse(
        error as AxiosError | Error,
        retryCount,
      );
    }
  }

  /**
   * Perform a GET request
   */
  async get<T>(
    url: string,
    params?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
    responseType?: AxiosRequestConfig["responseType"],
    config?: AxiosRequestConfig,
    noAuthHeader?: boolean,
  ): Promise<unknown> {
    return this.executeRequest<T>(
      "get",
      url,
      params,
      headers,
      responseType,
      config,
      noAuthHeader ? {} : undefined,
    );
  }

  /**
   * Perform a POST request
   */
  async post<T>(
    url: string,
    data?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
  ): Promise<unknown> {
    return this.executeRequest<T>("post", url, data, headers);
  }

  /**
   * Perform a PUT request
   */
  async put<T>(
    url: string,
    data?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
    config?: AxiosRequestConfig,
    noAuthHeader?: boolean,
  ): Promise<unknown> {
    return this.executeRequest<T>(
      "put",
      url,
      data,
      headers,
      undefined,
      config,
      noAuthHeader ? {} : undefined,
    );
  }

  /**
   * Perform a PATCH request
   */
  async patch<T>(
    url: string,
    data?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
  ): Promise<unknown> {
    return this.executeRequest<T>("patch", url, data, headers);
  }

  /**
   * Perform a DELETE request
   */
  async delete<T>(
    url: string,
    params?: HttpClientRequestPayload,
    headers?: HttpClientRequestHeaders,
  ): Promise<unknown> {
    return this.executeRequest<T>("delete", url, params, headers);
  }

  /**
   * Check if the user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasValidTokens() && !isTokenNearExpiry();
  }

  /**
   * Manually refresh the token
   */
  async refreshToken(): Promise<boolean> {
    try {
      await this.handleTokenRefresh();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Log out the user by clearing tokens and restarting auth flow
   */
  logout(): void {
    clearAllTokens();
    if (startPKCEFlowFunction) {
      startPKCEFlowFunction();
    }
  }
}
